import { createContext, useCallback, useEffect, useMemo, useState, type ReactNode } from "react";
import { getConfig } from "../api/client.ts";
import { BRAND_IDS, isBrand, readLastBrand, type BrandId } from "../config/brand.ts";

export type Theme = "light" | "dark" | "system";
export type ResolvedTheme = "light" | "dark";
export type VisionMode = "normal" | "protanopia" | "deuteranopia" | "tritanopia";
export type Density = "comfortable" | "compact" | "dense";
/** The brand that paints the chrome. Declared once, in config/brand.ts. */
export type ThemePack = BrandId;
/** Preferences -> Reveal characters. See docs/reveal-input-characters.md. */
export type RevealMode = "off" | "edges" | "all";

const THEME_PACK_LABELS: Record<ThemePack, string> = {
  instantsystems: "InstantSystems",
  maikoz: "Maikoz",
  ctlp: "CTLP",
};

/** For the SuperAdmin brand picker, in declaration order. */
export const THEME_PACKS: { value: ThemePack; label: string }[] =
  BRAND_IDS.map((value) => ({ value, label: THEME_PACK_LABELS[value] }));

export interface ThemeContextValue {
  theme: Theme;
  resolvedTheme: ResolvedTheme;
  setTheme: (theme: Theme) => void;
  visionMode: VisionMode;
  setVisionMode: (mode: VisionMode) => void;
  density: Density;
  setDensity: (density: Density) => void;
  themePack: ThemePack;
  /** Null clears an explicit pick, restoring endpoint/host resolution. */
  setThemePack: (pack: ThemePack | null) => void;
  /** Set by BrandSync from the active endpoint. Not persisted. */
  setDerivedThemePack: (pack: ThemePack | null) => void;
  /** The SuperAdmin pick and the host's pack, for useBrand to resolve in the same order as `themePack`. */
  pickedThemePack: ThemePack | null;
  hostThemePack: ThemePack | null;
  revealMode: RevealMode;
  setRevealMode: (mode: RevealMode) => void;
  revealTextarea: boolean;
  setRevealTextarea: (on: boolean) => void;
  /** Preferences -> Trim spaces on save. Default on. */
  autoTrim: boolean;
  setAutoTrim: (on: boolean) => void;
}

export const ThemeContext = createContext<ThemeContextValue | null>(null);

const STORAGE_KEY = "webadmincc-theme";
const VISION_STORAGE_KEY = "webadmincc-vision";
const DENSITY_STORAGE_KEY = "webadmincc-density";
const PACK_STORAGE_KEY = "webadmincc-theme-pack";
const VISION_MODES: VisionMode[] = ["normal", "protanopia", "deuteranopia", "tritanopia"];
const DENSITIES: Density[] = ["comfortable", "compact", "dense"];
const REVEAL_STORAGE_KEY = "webadmincc-reveal";
const REVEAL_TEXTAREA_STORAGE_KEY = "webadmincc-reveal-textarea";
const REVEAL_MODES: RevealMode[] = ["off", "edges", "all"];
const AUTO_TRIM_STORAGE_KEY = "webadmincc-auto-trim";

function getSystemTheme(): ResolvedTheme {
  if (typeof window === "undefined") return "light";
  return window.matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light";
}

export function ThemeProvider({ children }: { children: ReactNode }) {
  const [theme, setThemeState] = useState<Theme>(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored === "light" || stored === "dark" || stored === "system") return stored;
    } catch { /* noop */ }
    return "system";
  });

  const [systemTheme, setSystemTheme] = useState<ResolvedTheme>(getSystemTheme);

  const [visionMode, setVisionModeState] = useState<VisionMode>(() => {
    try {
      const stored = localStorage.getItem(VISION_STORAGE_KEY);
      if (stored && VISION_MODES.includes(stored as VisionMode)) return stored as VisionMode;
    } catch { /* noop */ }
    return "normal";
  });

  const setVisionMode = useCallback((m: VisionMode) => {
    setVisionModeState(m);
    try { localStorage.setItem(VISION_STORAGE_KEY, m); } catch { /* noop */ }
  }, []);

  const [density, setDensityState] = useState<Density>(() => {
    try {
      const stored = localStorage.getItem(DENSITY_STORAGE_KEY);
      if (stored && DENSITIES.includes(stored as Density)) return stored as Density;
    } catch { /* noop */ }
    return "comfortable";
  });

  const setDensity = useCallback((d: Density) => {
    setDensityState(d);
    try { localStorage.setItem(DENSITY_STORAGE_KEY, d); } catch { /* noop */ }
  }, []);

  // An explicit pick (SuperAdmin -> Impersonate -> Client) outranks everything
  // and is remembered; null means "no pick", not "instantsystems".
  const [explicitPack, setExplicitPackState] = useState<ThemePack | null>(() => {
    try {
      const stored = localStorage.getItem(PACK_STORAGE_KEY);
      if (isBrand(stored)) return stored;
    } catch { /* noop */ }
    return null;
  });

  // The tenant's own brand, pushed in by BrandSync once an endpoint is known.
  // Seeded from the last-seen brand so the first frame is already right; never
  // written here, so it cannot outlive the endpoint it came from.
  const [derivedPack, setDerivedThemePack] = useState<ThemePack | null>(readLastBrand);

  // Per-host default from the pod-rendered config. Placeholder values
  // (e.g. "__THEME_PACK__" when running the dev `public/config.json` directly
  // without the Vite middleware) are auto-rejected by the isBrand guard.
  const configPack = useMemo<ThemePack | null>(() => {
    try {
      const fromConfig = getConfig().ThemePack;
      if (isBrand(fromConfig)) return fromConfig;
    } catch { /* config not loaded yet — shouldn't happen post-bootstrap */ }
    return null;
  }, []);

  const themePack: ThemePack = explicitPack ?? derivedPack ?? configPack ?? "instantsystems";

  const [revealMode, setRevealModeState] = useState<RevealMode>(() => {
    try {
      const stored = localStorage.getItem(REVEAL_STORAGE_KEY);
      if (stored && REVEAL_MODES.includes(stored as RevealMode)) return stored as RevealMode;
    } catch { /* noop */ }
    return "off";
  });

  const setRevealMode = useCallback((m: RevealMode) => {
    setRevealModeState(m);
    try { localStorage.setItem(REVEAL_STORAGE_KEY, m); } catch { /* noop */ }
  }, []);

  // Off by default: free text legitimately contains whitespace, so markers are
  // noisiest in a textarea, and the Edges overlay there is a per-line problem
  // rather than two bands. See docs/reveal-input-characters.md.
  const [revealTextarea, setRevealTextareaState] = useState<boolean>(() => {
    try { return localStorage.getItem(REVEAL_TEXTAREA_STORAGE_KEY) === "on"; } catch { /* noop */ }
    return false;
  });

  const setRevealTextarea = useCallback((on: boolean) => {
    setRevealTextareaState(on);
    try { localStorage.setItem(REVEAL_TEXTAREA_STORAGE_KEY, on ? "on" : "off"); } catch { /* noop */ }
  }, []);

  // On by default: a leading/trailing space in a saved value is almost always a
  // paste accident. Off lets a value be saved verbatim — needed to reproduce a
  // dirty value while testing Reveal characters, and for the rare value where
  // the space is real. Only an explicit "off" disables it.
  const [autoTrim, setAutoTrimState] = useState<boolean>(() => {
    try { return localStorage.getItem(AUTO_TRIM_STORAGE_KEY) !== "off"; } catch { /* noop */ }
    return true;
  });

  const setAutoTrim = useCallback((on: boolean) => {
    setAutoTrimState(on);
    try { localStorage.setItem(AUTO_TRIM_STORAGE_KEY, on ? "on" : "off"); } catch { /* noop */ }
  }, []);

  /** Null clears the pick, handing the choice back to the endpoint and host. */
  const setThemePack = useCallback((p: ThemePack | null) => {
    setExplicitPackState(p);
    try {
      if (p) localStorage.setItem(PACK_STORAGE_KEY, p);
      else localStorage.removeItem(PACK_STORAGE_KEY);
    } catch { /* noop */ }
  }, []);

  const resolvedTheme: ResolvedTheme = theme === "system" ? systemTheme : theme;

  const setTheme = useCallback((t: Theme) => {
    setThemeState(t);
    try { localStorage.setItem(STORAGE_KEY, t); } catch { /* noop */ }
  }, []);

  // Listen to system color scheme changes
  useEffect(() => {
    const mq = window.matchMedia("(prefers-color-scheme: dark)");
    const handler = (e: MediaQueryListEvent) => setSystemTheme(e.matches ? "dark" : "light");
    mq.addEventListener("change", handler);
    return () => mq.removeEventListener("change", handler);
  }, []);

  // Apply data-theme, data-vision attributes and brand overrides to <html>
  useEffect(() => {
    const html = document.documentElement;
    html.setAttribute("data-theme", resolvedTheme);

    if (visionMode !== "normal") {
      html.setAttribute("data-vision", visionMode);
    } else {
      html.removeAttribute("data-vision");
    }

    if (density !== "comfortable") {
      html.setAttribute("data-density", density);
    } else {
      html.removeAttribute("data-density");
    }

    if (themePack !== "instantsystems") {
      html.setAttribute("data-theme-pack", themePack);
    } else {
      html.removeAttribute("data-theme-pack");
    }

    // Reveal characters. The textarea attribute is only written while a mode is
    // active — reveal.css requires both, and it keeps the DOM honest about
    // whether anything is actually revealed.
    if (revealMode !== "off") {
      html.setAttribute("data-reveal", revealMode);
    } else {
      html.removeAttribute("data-reveal");
    }

    if (revealMode !== "off" && revealTextarea) {
      html.setAttribute("data-reveal-textarea", "on");
    } else {
      html.removeAttribute("data-reveal-textarea");
    }
  }, [resolvedTheme, visionMode, density, themePack, revealMode, revealTextarea]);

  const value = useMemo<ThemeContextValue>(
    () => ({ theme, resolvedTheme, setTheme, visionMode, setVisionMode, density, setDensity, themePack, setThemePack, setDerivedThemePack, pickedThemePack: explicitPack, hostThemePack: configPack, revealMode, setRevealMode, revealTextarea, setRevealTextarea, autoTrim, setAutoTrim }),
    [theme, resolvedTheme, setTheme, visionMode, setVisionMode, density, setDensity, themePack, setThemePack, setDerivedThemePack, explicitPack, configPack, revealMode, setRevealMode, revealTextarea, setRevealTextarea, autoTrim, setAutoTrim],
  );

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}
